/**
 * The conversation's language as the widget holds it (ADR-128 Amendment 4,
 * ADR-110 Amendment 11).
 *
 * Three values, and the widget never decides between them on its own:
 *
 *   - the PAGE's language — `<html lang>`, the snippet's `language`, or the
 *     business's pinned `assistantLanguage` (config.ts): what the widget speaks
 *     before anyone has written anything, and what every request still sends
 *     as `language`, as the page's evidence;
 *   - the CONVERSATION's language — the `language` the last reply carried,
 *     which the backend's one decider chose (`common/languages.py`);
 *   - the visitor's CHOICE, made in the header's language control. It sticks:
 *     typing in another language does not undo it, because the control exists
 *     for the visitor who types one language and wants replies in another. It
 *     is sent with every request as `chosenLanguage` ('' for Automatic), and
 *     the field's presence is also how the backend knows this widget draws its
 *     booking in the reply's language.
 *
 * Only the five languages the widget, the booking and the emails all speak
 * can be chosen, and only those are adopted from a reply: a visitor writing
 * Polish is still answered in Polish, and the widget stays in the page's
 * language around it.
 */
import type { WidgetStrings } from './strings';

export const CHOOSABLE = ['en', 'it', 'fr', 'de', 'es'] as const;
export type Choosable = (typeof CHOOSABLE)[number];

/**
 * Each language's name for ITSELF — the one convention for a language picker:
 * someone looking for their language may not read the one the page is in.
 * No flags anywhere: a flag is a country, and English is not one country.
 */
export const ENDONYMS: Record<Choosable, string> = {
  en: 'English',
  it: 'Italiano',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
};

/** `it-IT`, `IT`, `it` → `it`; anything that is not one of the five → ''. */
export function choosable(tag: unknown): Choosable | '' {
  if (typeof tag !== 'string') return '';
  const base = tag.trim().split(/[-_]/)[0].toLowerCase();
  return (CHOOSABLE as readonly string[]).includes(base) ? (base as Choosable) : '';
}

/**
 * The language the widget draws itself in: the visitor's choice, else the
 * conversation's, else the page's. `page` may be anything a page declares;
 * the other two are already one of the five or ''.
 *
 * A TAG, not only a language, because dates are drawn with it: where the
 * choice or the conversation is the page's own language the page's region is
 * kept (`en-GB` writes "Wed 30 Sept", bare `en` writes "Wed, Sep 30"), and
 * English with no region to keep is British — the widget's own English copy
 * is, and so is the booking core's.
 */
export function drawnLanguage(chosen: Choosable | '', conversation: Choosable | '', page: string): string {
  const pick = chosen || conversation;
  if (!pick) return page;
  if (choosable(page) === pick) return page;
  return pick === 'en' ? 'en-GB' : pick;
}

/** The code the header's control shows: `EN`, or the page tag's base when it is none of the five. */
export function languageCode(tag: string): string {
  return (tag.split(/[-_]/)[0] || 'en').toUpperCase();
}

/** The line the widget draws in the conversation when the visitor chooses. */
export function choiceNotice(choice: Choosable | '', strings: WidgetStrings, name: string): string {
  const template = choice ? strings.languageChosen : strings.languageAutomaticOn;
  return template.replace('{name}', name);
}

const CHOICE_KEY = 'pweb-chat-language:';

/**
 * The choice this visitor made on this site before. `localStorage` per tenant,
 * read only once the panel has been opened (ChatWidget's `engaged`): nothing
 * is read from or written to a visitor's device before they ask for the chat.
 * Every access is inside try/catch — a private window, blocked storage or a
 * preview frame reads as "no choice".
 */
export function readChoice(tenantId: string): Choosable | '' {
  try {
    return choosable(window.localStorage.getItem(CHOICE_KEY + tenantId));
  } catch {
    return '';
  }
}

export function writeChoice(tenantId: string, choice: Choosable | ''): void {
  try {
    if (choice) window.localStorage.setItem(CHOICE_KEY + tenantId, choice);
    else window.localStorage.removeItem(CHOICE_KEY + tenantId);
  } catch {
    // Best-effort: the choice still holds for this page.
  }
}
